// packages/ui/src/tools/terminal/definition.ts
import type { ComponentType } from 'react';
import type { ToolDefinition, ToolModule, ToolViewProps } from '../types';
import { useToolsStore } from '../toolsStore';
import { terminalCloseGuard } from './closeGuard';

type TerminalState = { cwd: string; initialCommand?: string };

export const terminalToolDefinition: ToolDefinition<TerminalState> = {
  id: 'terminal',
  title: 'Terminal',
  icon: 'Terminal',
  instances: 'multi',
  capabilities: ['pty'],
  contributions: {
    directoryContextMenu: [
      {
        id: 'terminal.openHere',
        label: 'Open Terminal Here',
        icon: 'Terminal',
        run: ({ path }, host) => {
          host.openTool('terminal', { cwd: path });
        },
      },
    ],
    workspaceRootContextMenu: [
      {
        id: 'terminal.openAtRoot',
        label: 'Open Terminal at Workspace Root',
        icon: 'Terminal',
        run: ({ path }, host) => {
          host.openTool('terminal', { cwd: path });
        },
      },
    ],
    commands: [
      {
        id: 'terminal.new',
        title: 'Terminal: New',
        keybinding: 'CmdOrCtrl+Shift+`',
        run: (host) => {
          host.openTool('terminal');
        },
      },
      {
        id: 'terminal.toggle',
        title: 'Terminal: Toggle Panel',
        keybinding: 'CmdOrCtrl+`',
        run: () => {
          useToolsStore.getState().togglePanel();
        },
      },
    ],
    closeGuard: { isBlocking: terminalCloseGuard },
  },
  async load(): Promise<ToolModule<TerminalState>> {
    const mod = await import('./TerminalView');
    return {
      component: mod.TerminalView as unknown as ComponentType<ToolViewProps<TerminalState>>,
      createState: (_, initial) => ({
        cwd: (initial as { cwd?: string } | undefined)?.cwd ?? '',
        // One-shot: carried only on first open (e.g. from `/cmd ls -la`).
        initialCommand: (initial as { initialCommand?: string } | undefined)?.initialCommand,
      }),
      // serialize deliberately OMITS initialCommand so a restored terminal does
      // NOT re-run the command on the next launch.
      serialize: (s) => ({ cwd: s.cwd }),
      deserialize: (snap) => ({
        cwd: (snap as { cwd?: string } | null | undefined)?.cwd ?? '',
      }),
    };
  },
};
